import { parse } from "date-fns";
import type { IsoDate } from "@/ui-kit";
import { isIsoDate, isoDateOf, todayIso } from "@/ui-kit";

const HAS_ZONE = /([zZ]|[+-]\d{2}:?\d{2})$/;
/** "2026-10-10", or its UTC midnight in any of the server's spellings. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}(T00:00(:00(\.0+)?)?([zZ]|[+-]00:?00)?)?$/;

export const DateTimeUtils = {
  toUTCStartOfDay(date: Date): string {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
  },

  toUTCEndOfDay(date: Date): string {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    return new Date(Date.UTC(year, month, day, 23, 59, 59, 999)).toISOString();
  },

  /**
   * Parses an ISO datetime string (e.g., "2025-11-01T00:00:00.000Z")
   * as a local date, ignoring timezone conversion.
   * Used for date-only fields where timezone should not affect the day.
   *
   * @param isoString - ISO 8601 datetime string
   * @returns Date object at midnight in local timezone
   */
  parseISODateOnly(isoString: string): Date {
    return parse(isoString.split("T")[0], "yyyy-MM-dd", new Date());
  },

  /**
   * The API's date-only value ("2025-11-01T00:00:00.000Z") as its calendar day.
   * Some date fields are filled by the server with a moment instead (an SOR/MPS
   * inspection date defaults to the meeting start): anything but UTC midnight is
   * read as a moment and shown on the user's day.
   */
  fromApiDate(value: string | null | undefined): IsoDate | null {
    if (value && !DATE_ONLY.test(value)) return DateTimeUtils.localDayOf(value);
    const date = value?.slice(0, 10) ?? "";
    return isIsoDate(date) ? date : null;
  },

  /** A moment from the API. Zone-less values (LocalDateTime) are server UTC. */
  parseApiInstant(value: string | null | undefined): Date | null {
    if (!value) return null;
    const date = new Date(HAS_ZONE.test(value) ? value : `${value}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  },

  /** A moment's calendar day on the user's clock. */
  localDayOf(value: string | null | undefined): IsoDate | null {
    const date = DateTimeUtils.parseApiInstant(value);
    return date ? isoDateOf(date) : null;
  },

  /**
   * The latest day the server accepts as "in the past": it compares the day's
   * UTC midnight with its own UTC now. East of UTC that is yesterday for the
   * first hours of the user's day.
   */
  latestPastDay(): IsoDate {
    const local = todayIso();
    const utc = new Date().toISOString().slice(0, 10);
    return local < utc ? local : utc;
  },

  /** The API's day is today or later on the user's clock. */
  isTodayOrLater(value: string | null | undefined): boolean {
    const day = DateTimeUtils.fromApiDate(value);
    return !!day && day >= todayIso();
  },

  /** A calendar day as the API stores date-only values: UTC midnight. */
  toApiDate(date: IsoDate | null): string {
    return date ? `${date}T00:00:00.000Z` : "";
  },
};
